import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  Box,
  IconButton,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HouseIcon from '@mui/icons-material/House';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaptopIcon from '@mui/icons-material/Laptop';
import BusinessIcon from '@mui/icons-material/Business';
import { useNavigate, useParams } from 'react-router-dom';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import HttpHelper from '../../utils/HttpHelper';
import { useJobContext } from '../../JobContext';
import APICaller, { MethodType } from '../../components/common/APICaller';
import { useTypeSelector } from '../../hooks/useTypedSelector';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MobileGate from '../../components/common/MobileGate';
import { useLoading } from '../../contexts/LoadingContext';

interface Job {
  title: string;
  description: string;
  requirements: string;
  responsibilities: string;
  city: string;
  province: string;
  country: string;
  currentlyAvailable: boolean;
  datePosted: Date;
  salary: number;
  jobCategory: string;
  jobType: string;
  remote: boolean;
}

const JobDetails: React.FC = () => {
  const jobId = useParams().jobId;

  const [job, setJob] = useState<Job>();
  const [postedTimeAgo, setPostedTimeAgo] = useState('');
  const [hasApplied, setHasApplied] = useState(false);
  const [isCheckingApplication, setIsCheckingApplication] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const { authenticated, loading, keycloak, login, ensureAuth } = useAuth();
  const logged_in = authenticated;
  const is_hr = keycloak.hasResourceRole('hr');
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const { showLoading, hideLoading } = useLoading();

  // Check for existing auth session on mount (for SSO after login redirect)
  useEffect(() => {
    ensureAuth();
  }, [ensureAuth]);

  // Fetch job details (runs once per jobId)
  useEffect(() => {
    const updateJobDetails = async () => {
      showLoading('Loading Job Details...');
      try {
        const response = await HttpHelper.getPublic(`jobs/${jobId}`);
        if (response?.job) {
          const jobData = response.job;
          setJob({
            title: jobData.title,
            description: jobData.description,
            requirements: jobData.requirements,
            responsibilities: jobData.responsibilities,
            city: jobData.city,
            province: jobData.province,
            country: jobData.country,
            currentlyAvailable: jobData.currentlyAvailable,
            datePosted: jobData.datePosted,
            salary: jobData.salary,
            jobCategory: jobData.jobCategory,
            jobType: jobData.jobType,
            remote: jobData.remote,
          });
          setPostedTimeAgo(timeSincePosted(new Date(jobData.datePosted)));
        }
      } catch (error) {
        console.error('Error loading job details:', error);
      } finally {
        setHasLoadedOnce(true);
        hideLoading();
      }
    };

    if (jobId) updateJobDetails();
  }, [jobId]);

  // Check application status (runs when logged_in or jobId changes, no loading spinner)
  useEffect(() => {
    const checkApplicationStatus = async () => {
      if (!logged_in || !jobId) {
        setIsCheckingApplication(false);
        return;
      }
      try {
        const appliedJobs = await APICaller('/application/', MethodType.GET);
        const hasAlreadyApplied = appliedJobs.some(
          (job: { id: number }) => job.id === Number(jobId),
        );
        setHasApplied(hasAlreadyApplied);
      } catch (error) {
        console.error('Error checking application status:', error);
      } finally {
        setIsCheckingApplication(false);
      }
    };

    checkApplicationStatus();
  }, [logged_in, jobId]);

  const timeSincePosted = (date: Date) => {
    const now = new Date();
    const postedDate = new Date(date);
    const milliseconds = Math.abs(now.getTime() - postedDate.getTime());
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return '1 day ago';
    } else {
      return `${days} days ago`;
    }
  };
  const locationIcon = (
    <LocationOnIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );
  const accessTimeIcon = (
    <AccessTimeIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );
  const eventAvailableIcon = (
    <EventAvailableIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );
  const avTimerIcon = (
    <AvTimerIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );
  const houseIcon = (
    <HouseIcon sx={{ '&&': { color: 'var(--job-status-green)' } }} />
  );

  const { jobTypeMapping } = useJobContext();
  const inverseJobType = Object.fromEntries(
    Object.entries(jobTypeMapping).map(([key, value]) => [value, key]),
  );

  const checkProfileComplete = async (): Promise<boolean> => {
    try {
      // Preferred: backend returns completeness
      const res = await APICaller('/profile/completeness', MethodType.GET);
      setMissingFields(res.missing || []);
      return !!res.complete;
    } catch {
      // Fallback: mark incomplete if API not available
      setMissingFields(['profile']);
      return false;
    }
  };

  const handleApply = async () => {
    try {
      const complete = await checkProfileComplete();
      if (!complete) {
        setConfirmOpen(true);
        return;
      }

      await APICaller(`/application/jobs/${jobId}/start`, MethodType.POST);
      navigate(`/assessments/${jobId}`);
    } catch (error) {
      console.error('Error applying for job:', error);
    }
  };

  // Helper function to group missing fields into categories
  const getMissingFieldCategories = (fields: string[]): string[] => {
    const categories: string[] = [];
    const fieldsLower = fields.map(f => f.toLowerCase()).join(',');

    if (fieldsLower.includes('city') || fieldsLower.includes('province') ||
      fieldsLower.includes('country') || fieldsLower.includes('immigration') ||
      fieldsLower.includes('work eligibility')) {
      categories.push('Personal Information');
    }

    if (fieldsLower.includes('resume')) {
      categories.push('Resume');
    }

    if (fieldsLower.includes('lgbt') || fieldsLower.includes('age eligibility')) {
      categories.push('Voluntary Self Disclosure');
    }

    return categories;
  };

  // State for mobile gate
  const [showMobileGate, setShowMobileGate] = useState(false);

  // Handler for Login To Apply button
  const handleLoginToApply = () => {
    if (window.innerWidth < 900) {
      setShowMobileGate(true);
    } else {
      login();
    }
  };

  // Handler for Continue anyway
  const proceedToLogin = () => {
    setShowMobileGate(false);
    login();
  };

  // Render the appropriate button based on the application status
  const renderActionButton = () => {
    // HR users should not see Apply buttons
    if (is_hr) {
      return null;
    }

    if (!logged_in) {
      return (
        <>
          <Button
            variant="contained"
            sx={{
              '&&': {
                backgroundColor: 'var(--ttg-black)',
                '&:hover': { backgroundColor: 'var(--ttg-black)' },
              },
              marginTop: { xs: '10px', md: '20px' },
              marginRight: { xs: '0', md: '50px' },
              padding: { xs: '10px 24px', lg: '10px 40px' },
              maxHeight: '45px',
              fontSize: { xs: '0.75rem', md: '0.75rem', lg: '0.875rem' },
              width: { xs: 'fit-content', md: 'auto' },
              minWidth: { xs: '170px', md: 'auto' },
            }}
            onClick={handleLoginToApply}
          >
            Login To Apply
          </Button>
          <MobileGate
            isOpen={showMobileGate}
            onClose={() => setShowMobileGate(false)}
            onContinue={proceedToLogin}
          />
        </>
      );
    }

    if (isCheckingApplication) {
      return (
        <Button
          variant="contained"
          disabled
          sx={{
            marginTop: { xs: '10px', md: '20px' },
            marginRight: { xs: '0', md: '50px' },
            padding: { xs: '10px 24px', lg: '10px 40px' },
            maxHeight: '45px',
            fontSize: { xs: '0.75rem', md: '0.75rem', lg: '0.875rem' },
            width: { xs: 'fit-content', md: 'auto' },
            minWidth: { xs: '170px', md: 'auto' },
          }}
        >
          Loading...
        </Button>
      );
    }

    if (hasApplied) {
      return (
        <Button
          variant="contained"
          disabled
          sx={{
            '&&': {
              backgroundColor: '#cccccc',
            },
            marginTop: { xs: '10px', md: '20px' },
            marginRight: { xs: '0', md: '50px' },
            padding: { xs: '10px 24px', lg: '10px 40px' },
            maxHeight: '45px',
            fontSize: { xs: '0.75rem', md: '0.75rem', lg: '0.875rem' },
            width: { xs: 'fit-content', md: 'auto' },
            minWidth: { xs: '170px', md: 'auto' },
          }}
        >
          Already Applied
        </Button>
      );
    }

    return (
      <>
        <Button
          variant="contained"
          sx={{
            '&&': {
              backgroundColor: 'var(--ttg-green)',
              '&:hover': {
                backgroundColor: 'var(--button-hover-green)',
              },
            },
            marginTop: { xs: '10px', md: '20px' },
            marginRight: { xs: '0', md: '50px' },
            padding: { xs: '10px 24px', lg: '10px 40px' },
            maxHeight: '45px',
            fontSize: { xs: '0.875rem', md: '0.875rem', lg: '0.875rem' },
            width: { xs: 'fit-content', md: 'auto' },
            minWidth: { xs: '170px', md: '170px' },
          }}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Complete your profile'}
          </DialogTitle>
          <DialogContent>
            <Typography id="alert-dialog-description" sx={{ mb: 1 }}>
              You need to complete your profile before applying.
            </Typography>
            {missingFields.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Missing: {getMissingFieldCategories(missingFields).join(', ')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              sx={{
                width: '45%',
                backgroundColor: 'var(--ttg-green)',
                '&:hover': {
                  backgroundColor: 'var(--ttg-green)',
                },
              }}
              onClick={() => navigate('/profile')} // Adjust route if needed
            >
              Go to Profile
            </Button>
          </DialogActions>
        </Dialog>
      </>

    );
  };

  // Don't render content until both job details are loaded AND auth is initialized
  if (!hasLoadedOnce || loading) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: '24px',
      }}
    >
      <Card>
        <CardContent
          className="detailsCardContent"
          sx={{
            marginTop: { xs: '0px', md: '0px' },
            marginBottom: { xs: '16px', md: '24px' },
            marginLeft: { xs: '8px', sm: '12px', md: '16px' },
            marginRight: { xs: '8px', sm: '12px', md: '16px' },
          }}
        >
          {/* Back to List of Jobs button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              cursor: 'pointer',
              '&:hover': {
                '& .MuiTypography-root': {
                  textDecoration: 'underline',
                },
              },
            }}
            onClick={() => navigate('/jobs')}
          >
            <IconButton
              sx={{
                padding: '4px',
                marginRight: '8px',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: '1.5rem' }} />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              Back to list of jobs
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 1 }} mb={{ xs: 1, md: 1.5 }} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Typography
              alignSelf={{ xs: 'flex-start', md: 'end' }}
              align="left"
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                width: { xs: '100%', md: '70%' },
                fontWeight: 'bold',
                fontSize: { xs: '1.3rem', md: '1.75rem', lg: '2.125rem' }
              }}
            >
              {job ? job.title : ''}
            </Typography>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              width={{ xs: '100%', md: '35%' }}
              gap={{ xs: 1.5, md: 0 }}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              {renderActionButton()}
            </Box>
          </Stack>

          {/* Job Metadata - Mobile with separators - MOVED BEFORE BUTTON */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <Typography variant="body2" color="var(--ttg-black)" sx={{ fontSize: '0.75rem', textAlign: 'left' }}>
              {`${job?.city}, ${job?.province}, ${job?.country}`} | {job?.remote ? 'Remote' : 'On-site'} | {job && inverseJobType[job.jobType]}
            </Typography>
          </Box>

          {/* Mobile Button - AFTER METADATA */}
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            mb={2}
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            {renderActionButton()}
          </Box>

          {/* Job Metadata - Desktop with icons */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'row',
              alignItems: 'center',
              gap: 3,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
              <Typography variant="body2" color="var(--ttg-black)" sx={{ fontSize: '0.875rem' }}>
                {`${job?.city}, ${job?.province}, ${job?.country}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {job?.remote ? (
                <LaptopIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
              ) : (
                <BusinessIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
              )}
              <Typography variant="body2" color="var(--ttg-black)" sx={{ fontSize: '0.875rem' }}>
                {job?.remote ? 'Remote' : 'On-site'}
              </Typography>
            </Box>

            {job && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ color: 'var(--job-status-green)', fontSize: '1.25rem' }} />
                <Typography variant="body2" color="var(--ttg-black)" sx={{ fontSize: '0.875rem' }}>
                  {inverseJobType[job.jobType]}
                </Typography>
              </Box>
            )}

          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography align="left" variant="h5" paragraph sx={{ fontWeight: "bold" }}>
            Job Description
          </Typography>
          <Typography align="left" variant="body1" paragraph component="div">
            {job && (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(job.description || ''),
                }}
              />
            )}
          </Typography>
          <br />

          <Typography align="left" variant="h5" paragraph sx={{ fontWeight: "bold" }}>
            Responsibilities
          </Typography>
          <Typography align="left" variant="body1" paragraph component="div">
            {job && (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(job.responsibilities || ''),
                }}
              />
            )}
          </Typography>
          <br />

          <Typography align="left" variant="h5" paragraph sx={{ fontWeight: "bold" }}>
            Requirements
          </Typography>
          <Typography align="left" variant="body1" paragraph component="div">
            {job && (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(job.requirements || ''),
                }}
              />
            )}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default JobDetails;
